import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@components/ui/dialog";
import { CheckIcon } from "@heroicons/react/24/outline";
import { X } from "lucide-react";
import { useReactivateSubscription } from "./hooks/useReactivateSubscription";
import type { SettingsAvailablePlan } from "@pages/Settings";

interface PlanSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: SettingsAvailablePlan[];
  currentTierName: string | null;
  currentInterval: string | null;
  subscriptionPrefixId: string | null;
  isCancelled: boolean;
  hasSubscription: boolean;
  processor: string | null;
}

interface TierGroup {
  tierName: string;
  displayName: string;
  monthly: SettingsAvailablePlan | null;
  annual: SettingsAvailablePlan | null;
}

function groupPlansByTier(plans: SettingsAvailablePlan[]): TierGroup[] {
  const tiers = new Map<string, TierGroup>();

  for (const plan of plans) {
    if (!tiers.has(plan.tier_name)) {
      tiers.set(plan.tier_name, {
        tierName: plan.tier_name,
        displayName: plan.display_name,
        monthly: null,
        annual: null,
      });
    }
    const group = tiers.get(plan.tier_name)!;
    if (plan.interval === "month") {
      group.monthly = plan;
    } else {
      group.annual = plan;
    }
  }

  return Array.from(tiers.values());
}

/** Convert cents to monthly dollar display. Annual plans divide by 12. */
function monthlyPrice(plan: SettingsAvailablePlan): string {
  const cents = plan.interval === "year" ? plan.amount_cents / 12 : plan.amount_cents;
  return Math.round(cents / 100).toString();
}

export function PlanSelector({
  open,
  onOpenChange,
  plans,
  currentTierName,
  currentInterval,
  subscriptionPrefixId,
  isCancelled,
  hasSubscription,
  processor,
}: PlanSelectorProps) {
  const [isAnnual, setIsAnnual] = useState(currentInterval === "year" || !currentInterval);
  const { reactivate, isReactivating } = useReactivateSubscription({
    subscriptionPrefixId: subscriptionPrefixId ?? undefined,
  });

  const tierGroups = groupPlansByTier(plans);

  function getActivePlan(group: TierGroup): SettingsAvailablePlan | null {
    return isAnnual ? (group.annual ?? group.monthly) : (group.monthly ?? group.annual);
  }

  function isCurrentPlan(group: TierGroup): boolean {
    if (!hasSubscription || !currentTierName || !currentInterval) return false;
    const currentIsAnnual = currentInterval === "year";
    if (currentIsAnnual !== isAnnual) return false;
    return group.tierName === currentTierName;
  }

  function handleSelectPlan(plan: SettingsAvailablePlan, group: TierGroup) {
    const isCurrent = isCurrentPlan(group);

    if (isCurrent && isCancelled) {
      reactivate();
      return;
    }

    if (isCurrent && !isCancelled) {
      return;
    }

    if (hasSubscription && !isCancelled && subscriptionPrefixId && processor === "stripe") {
      window.location.href = `/subscriptions/${subscriptionPrefixId}/change_plan?plan=${plan.id}`;
    } else {
      window.location.href = `/subscriptions/new?plan=${plan.id}`;
    }
  }

  function getCtaText(group: TierGroup): string {
    const isCurrent = isCurrentPlan(group);
    if (isCurrent && isCancelled) return "Resume Plan";
    if (isCurrent) return "Current Plan";
    if (hasSubscription && !isCancelled && processor === "stripe") return "Switch Plan";
    if (hasSubscription && !isCancelled) return "Upgrade";
    return "Get Started";
  }

  function isCtaDisabled(group: TierGroup): boolean {
    return isCurrentPlan(group) && !isCancelled;
  }

  const isHighlighted = (tierName: string) => tierName === "growth";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-[820px] p-0 gap-0 bg-white border border-[#E2E1E0] rounded-lg shadow-[0px_4px_4px_-1px_rgba(12,12,13,0.1),0px_4px_4px_-1px_rgba(12,12,13,0.05)]"
      >
        <DialogClose className="absolute right-4 top-4 rounded-sm transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10">
          <X className="h-4 w-4 text-[#74767A]" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <DialogHeader className="px-8 pt-6 pb-0">
          <DialogTitle className="font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#0F1113]">
            {hasSubscription ? "Change Plan" : "Choose a Plan"}
          </DialogTitle>
          <DialogDescription className="font-['Plus_Jakarta_Sans'] text-sm text-[#74767A] pt-1">
            {hasSubscription
              ? "Switch to a different plan or billing interval."
              : "Select a plan to get started."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 pt-4 pb-6" data-testid="plan-selector">
          {/* Toggle */}
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex items-center bg-[#F5F5F4] border border-neutral-200 rounded-lg p-1">
              <button
                onClick={() => setIsAnnual(false)}
                data-testid="monthly-toggle"
                className={`px-4 py-2 text-sm rounded-md transition-all font-['Plus_Jakarta_Sans'] ${
                  !isAnnual
                    ? "bg-white shadow-sm font-medium text-[#2E3238]"
                    : "text-[#74767A]"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                data-testid="annual-toggle"
                className={`px-4 py-2 text-sm rounded-md transition-all font-['Plus_Jakarta_Sans'] ${
                  isAnnual
                    ? "bg-white shadow-sm font-medium text-[#2E3238]"
                    : "text-[#74767A]"
                }`}
              >
                Annual
              </button>
            </div>
            {isAnnual && (
              <span className="ml-3 bg-[#eaf5f3] text-[#5f7e78] text-xs font-medium px-3 py-1 rounded-full font-['Plus_Jakarta_Sans']">
                Save 25%
              </span>
            )}
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tierGroups.map((group) => {
              const plan = getActivePlan(group);
              if (!plan) return null;

              const highlighted = isHighlighted(group.tierName);
              const isCurrent = isCurrentPlan(group);

              return (
                <div
                  key={group.tierName}
                  data-testid={`plan-card-${group.tierName}`}
                  className={`relative rounded-xl flex flex-col p-5 transition-all ${
                    highlighted
                      ? "border-2 border-[#3748B8] shadow-md"
                      : "border border-neutral-200"
                  } ${isCurrent ? "bg-[#F8F9FF]" : "bg-white"}`}
                >
                  {highlighted && !isCurrent && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-[#3748B8] text-white text-xs font-medium px-2.5 py-0.5 rounded-full font-['Plus_Jakarta_Sans']">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {isCurrent && !isCancelled && (
                    <div className="absolute top-3 right-3" data-testid="current-plan-badge">
                      <span className="bg-[#2E9E72] text-white text-xs font-medium px-2.5 py-0.5 rounded-full font-['Plus_Jakarta_Sans']">
                        Current Plan
                      </span>
                    </div>
                  )}

                  {/* Plan Name */}
                  <h3 className="font-['Plus_Jakarta_Sans'] font-semibold text-lg text-[#2E3238]">
                    {group.displayName}
                  </h3>

                  {/* Price */}
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-[#2E3238] font-['Plus_Jakarta_Sans']">
                      ${monthlyPrice(plan)}
                    </span>
                    <span className="text-sm text-[#74767A] font-['Plus_Jakarta_Sans']">/mo</span>
                    {isAnnual && (
                      <p className="text-xs text-[#96989B] font-['Plus_Jakarta_Sans'] mt-0.5">
                        billed annually
                      </p>
                    )}
                  </div>

                  {/* Credits */}
                  <p className="text-sm text-[#74767A] font-['Plus_Jakarta_Sans'] mt-2">
                    {plan.credits.toLocaleString()} credits/month
                  </p>

                  {/* Features */}
                  {plan.features.length > 0 && (
                    <ul className="mt-3 space-y-2 flex-grow">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <CheckIcon className="h-4 w-4 text-[#2E9E72] flex-shrink-0" />
                          <span className="text-sm text-[#74767A] font-['Plus_Jakarta_Sans']">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(plan, group)}
                    disabled={isCtaDisabled(group) || isReactivating}
                    data-testid={`plan-cta-${group.tierName}`}
                    className={`mt-4 w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all font-['Plus_Jakarta_Sans'] ${
                      isCtaDisabled(group)
                        ? "bg-neutral-100 text-[#96989B] cursor-not-allowed"
                        : highlighted
                          ? "bg-[#3748B8] text-white hover:bg-[#2d3a9a]"
                          : "bg-[#2E3238] text-white hover:bg-[#1a1e22]"
                    } disabled:opacity-50`}
                  >
                    {isReactivating && isCurrent && isCancelled
                      ? "Resuming..."
                      : getCtaText(group)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
